import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "./axios"; // Your configured Axios instance
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

// Types
interface EtablissementQueryParams {
  page?: number;
  per_page?: number;
  search?: string;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
}

interface TEtablissement {
  id: number;
  name: string;
  signs?: any[]; // Replace with your actual sign type
  created_at?: string;
  updated_at?: string;
}

export type { TEtablissement };

interface RequestEtablissement {
  name: string;
}

interface PaginationData {
  current_page: number;
  per_page: number;
  total: number;
  last_page: number;
}

// React Query Keys
export const etablissementKeys = {
  all: ['etablissements'] as const,
  list: (page: number, per_page: number, params: Omit<EtablissementQueryParams, 'page' | 'per_page'>) => 
    ['etablissements', 'list', page, per_page, params] as const,
  detail: (id: number) => ['etablissements', 'detail', id] as const,
  etablissement: (id: number) => [...etablissementKeys.all, id] as const,
  signs: (id: number) => [...etablissementKeys.all, 'signs', id] as const,
  options: () => [...etablissementKeys.all, 'options'] as const,
};

/**
 * Le référentiel complet des enseignes, pour alimenter un sélecteur.
 *
 * FETCHÉ EN ENTIER, DÉLIBÉRÉMENT — et c'est l'inverse du choix fait pour les
 * centres (voir CentreAutocomplete, qui recherche côté serveur parce que le
 * catalogue pèse ~340 kB et fige l'onglet à l'ouverture d'un <Select>). Les deux
 * référentiels ne sont pas du même ordre de grandeur : une enseigne regroupe des
 * dizaines ou des centaines de centres, donc la table `etablissement` se compte
 * en dizaines de lignes de deux colonnes. Une recherche serveur ici coûterait
 * une requête par frappe pour économiser quelques kilo-octets.
 *
 * `per_page` est haut mais BORNÉ : l'endpoint pagine, et omettre le paramètre
 * ramènerait silencieusement les dix premières enseignes. Un sélecteur qui
 * n'offre que le début de l'alphabet est pire qu'un sélecteur lent, parce que
 * rien à l'écran ne dit qu'il manque des lignes.
 */
export const useEtablissementOptions = (enabled = true) => {
  const { data, error, ...res } = useQuery({
    queryKey: etablissementKeys.options(),
    queryFn: async (): Promise<TEtablissement[]> => {
      const response = await axios.get('/etablissements', {
        params: { page: 1, per_page: 500, sort_by: 'name', sort_order: 'asc' },
      });
      const payload = response?.data?.data;
      return Array.isArray(payload) ? (payload as TEtablissement[]) : [];
    },
    enabled,
    // Un référentiel de marques ne bouge pas pendant une session de travail.
    staleTime: 5 * 60 * 1000,
  });

  return { etablissements: data ?? [], error, ...res };
};

// Fetch all etablissements with pagination and search
export const useGetAllEtablissements = (params: EtablissementQueryParams = {}) => {
  const { page = 1, per_page = 10, ...queryParams } = params;
  
  const { data, error, ...res } = useQuery({
    queryKey: etablissementKeys.list(page, per_page, queryParams),
    queryFn: async (): Promise<{ data: TEtablissement[], pagination: PaginationData }> => {
      const response = await axios.get('/etablissements', { 
        params: { page, per_page, ...queryParams }
      });
      console.log(response);
      return {
        data: response?.data.data,
        pagination: {
          current_page: response?.data.current_page,
          per_page: response?.data.per_page,
          total: response?.data.total,
          last_page: response?.data.last_page,
        }
      };
    },
  });

  return { 
    etablissements: data?.data ?? [], 
    pagination: data?.pagination,
    error, 
    ...res 
  };
};

// Get one etablissement by ID
export const useGetEtablissement = (id: number) => {
  return useQuery<TEtablissement>({
    queryKey: etablissementKeys.etablissement(id),
    queryFn: async () => {
      const res = await axios.get(`/etablissements/${id}`);
      return res.data.data;
    },
    enabled: !!id,
  });
};

// Create an etablissement
export const useCreateEtablissement = (
  onClose?: () => void,
  initEtablissement?: () => void
) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (newEtablissement: RequestEtablissement) => {
      const response = await axios.post("/etablissements", newEtablissement);
      return response.data;
    },
    onSuccess: () => {
      onClose && onClose();
      initEtablissement && initEtablissement();
      
      // Invalidate all etablissement-related queries to ensure fresh data
      queryClient.invalidateQueries({ queryKey: etablissementKeys.all });
      
      toast.success(t("etablissement.createSuccess", "Etablissement created successfully"));
    },
    onError: (err: any) => {
      if (err?.response?.data?.status === 'error') {
        if (err?.response?.data?.errors?.name) {
          toast.error(t("etablissement.nameExists", "An etablissement with this name already exists"));
        } else if (err?.response?.data?.message) {
          toast.error(err.response.data.message);
        } else {
          toast.error(t("etablissement.createError", "Error creating etablissement"));
        }
      } else {
        toast.error(t("etablissement.createError", "Error creating etablissement"));
      }
    },
  });
};

// Update an etablissement
export const useUpdateEtablissement = (
  etablissementId: number,
  onClose?: () => void,
  initEtablissement?: () => void
) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (updatedEtablissement: RequestEtablissement) => {
      const response = await axios.put(`/etablissements/${etablissementId}`, updatedEtablissement);
      return response.data;
    },
    onSuccess: () => {
      onClose && onClose();
      initEtablissement && initEtablissement();
      
      // Invalidate both the specific etablissement and all etablissement lists
      queryClient.invalidateQueries({ queryKey: etablissementKeys.detail(etablissementId) });
      queryClient.invalidateQueries({ queryKey: etablissementKeys.all });
      
      toast.success(t("etablissement.updateSuccess", "Etablissement updated successfully"));
    },
    onError: (err: any) => {
      if (err?.response?.data?.status === 'error') {
        if (err?.response?.data?.errors?.name) {
          toast.error(t("etablissement.nameExists", "An etablissement with this name already exists"));
        } else if (err?.response?.data?.message) {
          toast.error(err.response.data.message);
        } else {
          toast.error(t("etablissement.updateError", "Error updating etablissement"));
        }
      } else {
        toast.error(t("etablissement.updateError", "Error updating etablissement"));
      }
    },
  });
};

// Delete an etablissement
export const useDeleteEtablissement = (onSuccess?: () => void) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (etablissementId: number) => {
      const response = await axios.delete(`/etablissements/${etablissementId}`);
      return response.data;
    },
    onSuccess: () => {
      onSuccess && onSuccess();
      
      // Invalidate all etablissement-related queries to ensure fresh data
      queryClient.invalidateQueries({ queryKey: etablissementKeys.all });
      
      toast.success(t("etablissement.deleteSuccess", "Etablissement deleted successfully"));
    },
    onError: (err: any) => {
      if (err?.response?.data?.status === 'error') {
        if (err?.response?.data?.message) {
          toast.error(err.response.data.message);
        } else {
          toast.error(t("etablissement.deleteError", "Error deleting etablissement"));
        }
      } else {
        toast.error(t("etablissement.deleteError", "Error deleting etablissement"));
      }
    },
  });
};

// Get signs for a specific etablissement
export const useGetEtablissementSigns = (etablissementId: number) => {
  return useQuery({
    queryKey: etablissementKeys.signs(etablissementId),
    queryFn: async () => {
      const res = await axios.get(`/etablissements/${etablissementId}/signs`);
      return res.data.data;
    },
    enabled: !!etablissementId,
  });
};